import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  ErrorGenericSchema,
  GenericAccountId,
  GenericUUID,
  ProjectSuggestionSchema,
  ProjectSuggestionsWebhookPayloadSchema,
  TriggerProjectSuggestionsSchema,
  UpdateProjectSuggestionStatusSchema,
} from '../models';

extendZodWithOpenApi(z);

const c = initContract();

export const apiProjectSuggestions = c.router({
  // ================================================
  // WEBHOOK (internal, called by Lambda)
  // ================================================

  projectSuggestionsWebhook: {
    method: 'POST',
    path: `/1.0/ai/webhooks/project_suggestions`,
    description:
      'Webhook endpoint called by the project creator Lambda when project suggestions are generated for a new account.',
    summary: 'Project suggestions webhook',
    body: ProjectSuggestionsWebhookPayloadSchema,
    responses: {
      200: z.object({
        message: z.string(),
      }),
      400: ErrorGenericSchema,
      500: ErrorGenericSchema,
    },
  },

  // ================================================
  // FRONTEND API
  // ================================================

  getProjectSuggestions: {
    method: 'GET',
    path: `/1.0/accounts/:accountId/project-suggestions`,
    description: 'Get pending project suggestions for an account. Requires admin/owner role.',
    summary: 'Get project suggestions',
    pathParams: z.object({
      accountId: GenericAccountId.openapi({
        description: 'TIC account ID',
      }),
    }),
    responses: {
      200: z.array(ProjectSuggestionSchema),
      403: ErrorGenericSchema,
      500: ErrorGenericSchema,
    },
  },

  triggerProjectSuggestions: {
    method: 'POST',
    path: `/1.0/project-suggestions/generate`,
    description:
      'Trigger AI generation of project suggestions for a new account. Fire-and-forget — the Lambda will call the webhook when done.',
    summary: 'Trigger project suggestions generation',
    body: TriggerProjectSuggestionsSchema,
    responses: {
      202: z.object({
        message: z.string(),
      }),
      400: ErrorGenericSchema,
      403: ErrorGenericSchema,
      500: ErrorGenericSchema,
    },
  },

  updateProjectSuggestionStatus: {
    method: 'PATCH',
    path: `/1.0/accounts/:accountId/project-suggestions/:id`,
    description:
      'Update the status of a project suggestion set (accept or dismiss). Requires admin/owner role.',
    summary: 'Update project suggestion status',
    pathParams: z.object({
      accountId: GenericAccountId.openapi({
        description: 'TIC account ID',
      }),
      id: GenericUUID.openapi({
        description: 'Project suggestion set ID',
      }),
    }),
    body: UpdateProjectSuggestionStatusSchema,
    responses: {
      200: ProjectSuggestionSchema,
      403: ErrorGenericSchema,
      404: ErrorGenericSchema,
      500: ErrorGenericSchema,
    },
  },
});
